import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import VendorLayout from "@/components/vendor_portal/VendorLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CheckCircle2, Clock, Inbox, ShoppingBag, MessageSquare } from "lucide-react";

/**
 * Vendor dashboard landing (Prompt 3). Renders a welcome card + placeholder
 * stat tiles. All CRUD (requests, catalog, orders) is deferred per the spec.
 */
export default function VendorDashboardPage() {
  const { user } = useAuth();
  const [vendor, setVendor] = useState(null);

  useEffect(() => {
    api.get("/vendor/me").then(({ data }) => setVendor(data.vendor)).catch(() => {});
  }, []);

  return (
    <VendorLayout>
      <section data-testid="vendor-dashboard">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
              Vendor Dashboard
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-3 leading-tight">
              Welcome{vendor?.company_name ? `, ${vendor.company_name}` : ""}.
            </h1>
            <p className="mt-3 text-[#4A4A4A] max-w-2xl">
              Your account is live. Product requests, catalog management, and order fulfillment tools are launching soon — we&rsquo;ll email you the moment each area goes live.
            </p>
          </div>

          {vendor?.status && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A422D]/10 border border-[#1A422D]/30 text-[11px] uppercase tracking-[0.24em] font-semibold text-[#1A422D]">
              <CheckCircle2 size={14} /> {vendor.status}
            </div>
          )}
        </div>

        {/* Stat tiles */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile icon={<Inbox size={20} />} label="Open Requests" value="—" hint="Coming soon" />
          <StatTile icon={<ShoppingBag size={20} />} label="Active Orders" value="—" hint="Coming soon" />
          <StatTile icon={<MessageSquare size={20} />} label="Unread Messages" value="—" hint="Coming soon" />
          <StatTile icon={<Clock size={20} />} label="Response Time" value="—" hint="Coming soon" />
        </div>

        {/* Getting started */}
        <div className="mt-12 p-8 bg-white border border-[#E5E3D8]">
          <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">
            Getting Started
          </div>
          <h2 className="font-serif text-2xl text-[#1A422D] mt-2">
            What&rsquo;s next while we build out your workspace.
          </h2>
          <ul className="mt-6 space-y-3 text-sm text-[#4A4A4A]">
            <li>&bull; We&rsquo;ll invite you to upload your product catalog once the Catalog tab is live.</li>
            <li>&bull; Your vendor profile is visible to all pro shops in our network.</li>
            <li>&bull; Reply to any of our emails to reach the platform team directly.</li>
          </ul>
          <div className="mt-6">
            <Link
              to="/vendor/settings"
              data-testid="vendor-dashboard-settings-link"
              className="inline-flex items-center gap-2 px-6 py-2.5 border border-[#1A422D] text-[#1A422D] text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
            >
              Review Profile
            </Link>
          </div>
        </div>
      </section>
    </VendorLayout>
  );
}

function StatTile({ icon, label, value, hint }) {
  return (
    <div className="p-5 bg-white border border-[#E5E3D8]">
      <div className="flex items-center gap-3 text-[#B59A5A]">
        {icon}
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold">{label}</div>
      </div>
      <div className="mt-3 font-serif text-3xl text-[#1A422D]">{value}</div>
      <div className="mt-1 text-xs text-[#9A9A9A]">{hint}</div>
    </div>
  );
}
